import { Button, Col, DatePicker, Form, Pagination, Row, Select, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageTitle from 'components/page-title';
import { API } from 'service/api-route';
import { LEDGER_KIND_OPTIONS } from 'pages/star-shared/labels';
import { LedgerKind, LedgerRow } from 'pages/star-shared/types';
import { TABLE_EMPTY, formatDate, formatDateTime, personName } from 'pages/star-shared/utils';
import { usePagedList } from 'pages/star-shared/use-paged-list';
import StarAmount from 'pages/star-shared/components/star-amount';
import {
  Ellipsis,
  LedgerKindTag,
  NoWrap,
  UserCell,
} from 'pages/star-shared/components/status-tags';
import UserPicker, { UserPickerValue } from 'pages/star-shared/components/user-picker';

const LIMIT = 20;
type RangeValue = [Dayjs | null, Dayjs | null] | null;

interface Filter {
  userId?: number;
  kind?: LedgerKind;
  from?: string;
  to?: string;
  page: number;
  limit: number;
}

interface FormValue {
  user?: UserPickerValue;
  kind?: LedgerKind;
  range?: RangeValue;
}

// ref_type do backend ghi (không đổi được) → nhãn tiếng Việt cho cột "Nguồn".
const REF_TYPE_LABEL: Record<string, string> = {
  mission_progress: 'Nhiệm vụ',
  star_user_item: 'Đơn quà',
  star_penalty: 'Phạt',
  star_recruit: 'Tuyển dụng',
  star_dance_score: 'Chấm nhảy',
};

const refLabel = (r: LedgerRow) => {
  if (!r.refType) return null;
  const name = REF_TYPE_LABEL[r.refType] || r.refType;
  return r.refId ? `${name} #${r.refId}` : name;
};

const columns: ColumnsType<LedgerRow> = [
  { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
  {
    title: 'Thời gian',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 160,
    render: (v) => <NoWrap>{formatDateTime(v)}</NoWrap>,
  },
  { title: 'Người dùng', key: 'user', width: 220, render: (_, r) => <UserCell user={r.user} /> },
  { title: 'Loại', key: 'kind', width: 120, render: (_, r) => <LedgerKindTag value={r.kind} /> },
  {
    title: 'Số Sao',
    key: 'amount',
    width: 110,
    align: 'right',
    render: (_, r) => <StarAmount value={r.amount} signed />,
  },
  {
    title: 'Số dư sau',
    key: 'balanceAfter',
    width: 110,
    align: 'right',
    render: (_, r) => <StarAmount value={r.balanceAfter} strong={false} />,
  },
  {
    title: 'Nguồn',
    key: 'ref',
    width: 150,
    render: (_, r) => <Ellipsis value={refLabel(r)} width={200} />,
  },
  {
    title: 'Đối tác',
    key: 'counterparty',
    width: 150,
    render: (_, r) => (r.counterparty ? personName(r.counterparty) : '—'),
  },
  {
    title: 'Người thao tác',
    key: 'actor',
    width: 150,
    render: (_, r) => (r.actor ? personName(r.actor) : 'Hệ thống'),
  },
  { title: 'Ghi chú', key: 'note', render: (_, r) => <Ellipsis value={r.note} width={240} /> },
  {
    title: 'Ngày VN',
    dataIndex: 'vnDate',
    key: 'vnDate',
    width: 130,
    // vnDate là "YYYY-MM-DD" → cùng định dạng DD/MM/YYYY với cột Thời gian.
    render: (v) => <NoWrap>{formatDate(v)}</NoWrap>,
  },
];

const StarLedgerPage = () => {
  const [searchParams] = useSearchParams();
  const initialUserId = Number(searchParams.get('userId')) || undefined;
  const initialName = searchParams.get('name') || undefined;
  const [form] = Form.useForm<FormValue>();
  const [filter, setFilter] = useState<Filter>({ userId: initialUserId, page: 1, limit: LIMIT });
  const { items, total, loading } = usePagedList<LedgerRow>(API.starsLedger, filter);

  const initialValues: FormValue = {
    user: initialUserId
      ? { value: initialUserId, label: initialName || `#${initialUserId}` }
      : undefined,
    kind: undefined,
    range: undefined,
  };

  const submit = (value: FormValue) => {
    setFilter({
      userId: value.user?.value,
      kind: value.kind,
      from: value.range?.[0] ? dayjs(value.range[0]).format('YYYY-MM-DD') : undefined,
      to: value.range?.[1] ? dayjs(value.range[1]).format('YYYY-MM-DD') : undefined,
      page: 1,
      limit: LIMIT,
    });
  };

  const clear = () => {
    form.setFieldsValue({ user: undefined, kind: undefined, range: undefined });
    setFilter({ page: 1, limit: LIMIT });
  };

  return (
    <div>
      <PageTitle title="Lịch sử giao dịch" />
      <Form
        form={form}
        layout="vertical"
        onFinish={submit}
        initialValues={initialValues}
        className="custom-form"
        style={{ marginBottom: 16 }}
      >
        {/* Cột nút xl=7: "Tìm kiếm" + "Xoá lọc" cần ~230px; ở xl=5 (~187px khi màn 1280) nút
            "Xoá lọc" thò ra ngoài form. flexWrap để màn hẹp hơn nữa thì xuống dòng thay vì tràn. */}
        <Row gutter={[16, 8]}>
          <Col xl={6} lg={8} md={12} xs={24}>
            <Form.Item name="user" label="Người dùng">
              <UserPicker />
            </Form.Item>
          </Col>
          <Col xl={4} lg={6} md={12} xs={24}>
            <Form.Item name="kind" label="Loại giao dịch">
              <Select allowClear placeholder="Tất cả" options={LEDGER_KIND_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xl={7} lg={10} md={12} xs={24}>
            <Form.Item name="range" label="Khoảng ngày (ngày VN)">
              <DatePicker.RangePicker format="DD/MM/YYYY" placeholder={['Từ', 'Đến']} />
            </Form.Item>
          </Col>
          <Col xl={7} lg={24} md={12} xs={24}>
            <Form.Item label=" ">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                  Tìm kiếm
                </Button>
                <Button icon={<CloseOutlined />} onClick={clear}>
                  Xoá lọc
                </Button>
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <div className="custom-table">
        <Table
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={items}
          loading={loading}
          locale={TABLE_EMPTY}
          scroll={{ x: 1620 }}
        />
        {total > LIMIT && (
          <Pagination
            showSizeChanger={false}
            current={filter.page}
            onChange={(page) => setFilter((prev) => ({ ...prev, page }))}
            showLessItems
            total={total}
            pageSize={LIMIT}
          />
        )}
      </div>
    </div>
  );
};

export default StarLedgerPage;
